import React, { useEffect, useState } from 'react';
import { CocoEvent } from '@cotera/client/app/etc';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading, toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  ResourceSelectionModel,
  ResourceType,
} from '../selection/resource-selection.model';
import { FolderContentsPageModel } from '../../folder/folder-contents-page.model';

const renameFolderParamsSchema = z.object({
  name: z.string().min(1),
});

type Params = z.infer<typeof renameFolderParamsSchema>;

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

type RenameFolderActionClient = {
  resource: {
    folder: Pick<TenantedClient['resource']['folder'], 'rename'>;
  };
};

export class RenameFolderAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Rename Folder';
  /**
   * Acts on whatever resource is targeted, so it belongs to that resource's own
   * menu, not to a workspace-wide search. It stays registered and invokable —
   * `discrete` only keeps it out of the palette's list.
   */
  discrete = true;

  description = 'Rename a folder';
  inputSchema = renameFolderParamsSchema;
  group = 'app';
  icon = 'folder';

  private _lastRenamedFolderId: string | null = null;

  constructor(
    private readonly client: RenameFolderActionClient,
    private readonly modalManager: ModalManager
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTargetFolderId(context) !== null;
  }

  getPriority(context: ApplicableContext): number {
    const folderId = this.getTargetFolderId(context);
    if (folderId !== null) {
      return 20;
    }

    return 0;
  }

  shouldAsk(): boolean {
    return true;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const folderId = this.getTargetFolderId(context);
    if (folderId === null) {
      return err([{ t: 'error', message: 'No folder in scope' }]);
    }

    const result = await this.client.resource.folder.rename({
      folderId,
      name: payload.name.trim(),
    });

    if (result.isErr()) {
      const msg =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to rename folder';
      toast.error(msg);
      return err([{ t: 'error', message: msg }]);
    }

    const selection = context.targetedModelOfType(ResourceSelectionModel);
    if (selection !== null) {
      context.markAsNotTargeted(selection);
    }

    this._lastRenamedFolderId = folderId;

    toast.success('Folder renamed');
    return ok({});
  }

  track(): ActionTrackMetadata | null {
    if (this._lastRenamedFolderId === null) {
      return null;
    }
    return {
      event: CocoEvent.FolderRenamed,
      properties: { folderId: this._lastRenamedFolderId },
    };
  }

  ask(
    params: Partial<Params>,
    context: ExecuteContext
  ): AskResponse<Params, { t: string; message?: string }[]> {
    const folderId = this.getTargetFolderId(context);
    const initialName =
      params.name ?? this.getInitialFolderNameFromContext(context);
    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async () => {
          await onCancel();
          this.modalManager.pop();
        };

        this.modalManager.push({
          view: () => (
            <RenameFolderAskView
              folderId={folderId}
              initialName={initialName}
              onRespond={async (nextParams) => {
                await onRespond(nextParams);
                this.modalManager.pop();
              }}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }

  private getTargetFolderId(context: ApplicableContext): string | null {
    if (context.isInScope(ResourceSelectionModel)) {
      const selection = context.getInScopeModelsOfType(ResourceSelectionModel);
      if (
        selection.length === 1 &&
        selection[0]?.selections.length === 1 &&
        selection[0]?.getResourceIdsForType(ResourceType.Folder).length === 1
      ) {
        return (
          selection[0]?.getResourceIdsForType(ResourceType.Folder)[0] ?? null
        );
      }
    }

    const folderPageModel = context.targetedModelOfType(
      FolderContentsPageModel
    );

    if (folderPageModel === null || folderPageModel.folderId === null) {
      return null;
    }

    return folderPageModel.folderId;
  }

  private getInitialFolderNameFromContext(
    context: ExecuteContext
  ): string | undefined {
    if (context.isInScope(ResourceSelectionModel)) {
      const selection = context.getInScopeModelsOfType(ResourceSelectionModel);
      if (
        selection.length === 1 &&
        selection[0]?.selections.length === 1 &&
        selection[0]?.getResourceForType(ResourceType.Folder) !== undefined
      ) {
        return selection[0]?.getResourceForType(ResourceType.Folder)?.name;
      }
    }

    const folderPageModel = context.targetedModelOfType(
      FolderContentsPageModel
    );

    if (folderPageModel === null || folderPageModel.folderId === null) {
      return undefined;
    }

    const folderMeta =
      folderPageModel?.folderContentsDataWatchable.snapshot()?.folderMeta;
    if (folderMeta?.name !== undefined && folderMeta.name !== '') {
      return folderMeta.name;
    }

    return undefined;
  }
}

const RenameFolderAskView: React.FC<{
  folderId: string | null;
  initialName?: string;
  onRespond: (params: Params) => Promise<void>;
  onCancel: () => Promise<void>;
}> = ({ folderId, initialName, onRespond, onCancel }) => {
  const [name, setName] = useState(initialName ?? '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialName !== undefined) {
      setName(initialName);
    }
  }, [initialName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0 || loading || folderId === null) {
      return;
    }
    setLoading(true);
    await onRespond({ name: trimmed });
    setLoading(false);
  };

  return (
    <ModalPageView
      title="Rename folder"
      subtitle="Choose a new name for this folder"
      className="px-4 py-4"
    >
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-2">
          <label htmlFor="folder-name" className="text-sm font-medium">
            Folder name
          </label>
          <Input
            id="folder-name"
            autoFocus
            placeholder="Folder name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void onCancel()}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading || folderId === null || name.trim().length === 0}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Rename'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
